import React from 'react';
import { WeezHeader, Button, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import MainSettings from './components/mainSettings';
import { InternalResaleSettingsModel } from '../../../models';
import ResaleNotifications from './components/resaleNotifications';
import PrivateResale from './components/privateResale';
import { DEFAULT_TEMPLATE_IDS, getTemplatesLists } from './utils';
import { useForm, websiteField } from '../../../utils/validators';

export default function ResaleSettings({ organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [templates, setTemplates] = React.useState({ onResale: [], resold: [], resale: [] });
    const [loadingTemplates, setLoadingTemplates] = React.useState(true);
    const [isSubmitting, setSubmitting] = React.useState(false);

    const initialForm = {
        default_resale_widget: null,
        host_resale_link: null,
        identity_id: null,
        notify_ticket_on_resale: true,
        template_id_ticket_on_resale: DEFAULT_TEMPLATE_IDS.TICKET_ON_RESALE,
        notify_ticket_resold: true,
        template_id_ticket_resold: DEFAULT_TEMPLATE_IDS.TICKET_RESOLD,
        enable_private_resale: true,
        enabled_share_link: true,
        share_link_template: DEFAULT_TEMPLATE_IDS.RESALE_SHARE_LINK
    };

    const validators = React.useMemo(() => {
        return {
            host_resale_link: websiteField
        };
    }, []);

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(initialForm, validators);

    const [settings, loadingSettings] = InternalResaleSettingsModel.useApiModel({ organizationId: organization.id, eventId: event.id }, [organization.id, event.id]);

    React.useEffect(() => {
        InternalResaleSettingsModel.getTemplates({ organizationId: organization.id, eventId: event.id })
            .then(data => {
                setTemplates(getTemplatesLists(data));
            })
            .catch(() => {
                toast.error(t(`ticket.resales.settings.notifications.templates.error`));
            })
            .finally(() => setLoadingTemplates(false));
    }, [organization.id, event.id]);

    React.useEffect(() => {
        if (!settings) return;

        setFormObject({
            default_resale_widget: settings.default_resale_widget || null,
            host_resale_link: settings.host_resale_link || null,
            identity_id: settings.identity_id || null,
            notify_ticket_on_resale: settings.notify_ticket_on_resale !== undefined ? settings.notify_ticket_on_resale : true,
            template_id_ticket_on_resale: settings.template_id_ticket_on_resale || DEFAULT_TEMPLATE_IDS.TICKET_ON_RESALE,
            notify_ticket_resold: settings.notify_ticket_resold !== undefined ? settings.notify_ticket_resold : true,
            template_id_ticket_resold: settings.template_id_ticket_resold || DEFAULT_TEMPLATE_IDS.TICKET_RESOLD,
            enable_private_resale: settings.enable_private_resale !== undefined ? settings.enable_private_resale : true,
            enabled_share_link: settings.enabled_share_link !== undefined ? settings.enabled_share_link : true,
            share_link_template: settings.share_link_template || DEFAULT_TEMPLATE_IDS.RESALE_SHARE_LINK
        });
    }, [settings, setFormObject]);

    const handleSave = React.useCallback(async () => {
        if (!formValidate()) return;
        setSubmitting(true);
        try {
            const method = settings ? 'patch' : 'post';
            await InternalResaleSettingsModel.save(form, method, {
                organizationId: organization.id,
                eventId: event.id
            });
            toast.success(t('ticket.resales.settings.toast.success'));
        } catch (error) {
            console.error('[RESALE SETTINGS] Save error:', error);
            toast.error(t('ticket.resales.settings.toast.error'));
        } finally {
            setSubmitting(false);
        }
    }, [form, organization.id, event.id, settings]);

    return (
        <>
            <WeezHeader
                title={t('ticket.resales.settings.title')}
                rightComponent={[
                    <Button
                        key="save"
                        primary
                        label={t('ticket.common.action.save')}
                        onClick={handleSave}
                        disabled={loadingSettings || isSubmitting || Object.keys(formErrors).length > 0}
                    />
                ]}
            >
                {t('ticket.resales.settings.subtitle')}
            </WeezHeader>
            <MainSettings form={form} updateForm={setForm} organization={organization} event={event} loading={loadingSettings} formErrors={formErrors} />
            <ResaleNotifications
                form={form}
                updateForm={setForm}
                organization={organization}
                event={event}
                templates={templates}
                loadingTemplates={loadingTemplates}
                onToggleChange={(value, field) => setForm(f => ({ ...f, [field]: value }))}
                loading={loadingSettings}
            />
            <PrivateResale
                form={form}
                updateForm={setForm}
                organization={organization}
                event={event}
                loading={loadingSettings}
                templates={templates.resale || []}
                loadingTemplates={loadingTemplates}
            />
        </>
    );
}
